/* The annotator's review workspace (sections 3–7).
 *
 * FULL-HEIGHT, NO PAGE SCROLL (3.1.1). The layout is a column: header, task
 * strip, a main row that flexes, and the timeline pinned to the bottom. Every
 * child carries `min-h-0` / `min-w-0`, because a flex child defaults to
 * min-content and one long timeline lane will otherwise push the whole page
 * sideways — which is how this exact layout broke once already.
 *
 * SELECTION AND THE PLAYHEAD ARE SEPARATE (5.1.4). Clicking a block selects it
 * without seeking. Playing INTO a different sub-task selects it (3.3.5) — but
 * never steals focus from a field being typed in, so a boundary crossing
 * cannot swallow a half-typed word.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router";
import { Player, type PlayerHandle } from "../components/Player";
import { Timeline } from "../components/Timeline";
import { Inspector, SubtaskActions } from "../components/Inspector";
import { SubtaskMap, TaskCard, TaskStrip } from "../components/TaskStrip";
import { ChecklistDrawer, ShortcutSheet } from "../components/Drawers";
import { Button, Spinner, useToast } from "../components/ui";
import { api } from "../api/client";
import { blockingIssues, rulesFrom, submitGate } from "../lib/checks";
import { counts, money, preciseClock, subtaskTitle } from "../lib/format";
import { displayName } from "../lib/featured";
import { loopSpanFor } from "../lib/playback";
import { backToQueue } from "../lib/queuereturn";
import { useJob } from "../lib/useJob";
import type { Highlights, JobAccuracy, JobDetail, Subtask, Task } from "../api/types";
import { currentRole, currentSession } from "../lib/auth";
import { pct } from "../lib/accuracy";

const MIN_SPLIT_MARGIN = 0.4; // 5.3.1

export function Workspace() {
  const { id } = useParams();
  const jobId = Number(id);
  const navigate = useNavigate();
  // The queue's page, carried in on the URL the row linked to, so "← Back"
  // returns to it. Read here rather than in the two Back links so both use the
  // same value. See lib/queuereturn.ts.
  const location = useLocation();
  const toast = useToast();

  const onError = useCallback((m: string) => toast(m, "error"), [toast]);
  const job = useJob(jobId, onError);

  /* QA ONLY: this video's row of the Accuracy page and each sub-task's match
     with the ground truth — the same endpoint and arithmetic as /accuracy, so
     the video screen shows exactly what the page shows. */
  const isQa = currentRole() === "qa";
  const [jobAcc, setJobAcc] = useState<JobAccuracy | null>(null);
  useEffect(() => {
    if (!isQa || !Number.isFinite(jobId)) return;
    let live = true;
    api.jobAccuracy(jobId).then((r) => { if (live) setJobAcc(r); }).catch(() => { /* page works without it */ });
    return () => { live = false; };
  }, [isQa, jobId]);
  const gtMatch = useMemo(
    () => new Map((jobAcc?.subtasks ?? []).map((m) => [m.subtask_id, m.match])), [jobAcc]);

  /* OBJECT AND HAND HIGHLIGHTS (31 Oct 2026): boxes over the picture, every
     role. Loaded once per video; a video without them simply has no button. */
  const [highlights, setHighlights] = useState<Highlights | null>(null);
  useEffect(() => {
    if (!Number.isFinite(jobId)) return;
    let live = true;
    setHighlights(null);
    api.highlights(jobId).then((r) => { if (live) setHighlights(r); }).catch(() => { /* works without it */ });
    return () => { live = false; };
  }, [jobId]);

  /* HIGHLIGHTS ON / OFF IS ONE SWITCH FOR EVERYONE (4 Nov 2026, manager's
     review): OFF on any video turns them off on every video, for every user,
     until someone turns them back on. Read when a video opens; the button
     writes it. Shown at once, put back if the server refuses. */
  const [highlightsOn, setHighlightsOn] = useState(true);
  useEffect(() => {
    let live = true;
    api.settings().then((r) => { if (live) setHighlightsOn(r.highlights_enabled); })
      .catch(() => { /* default: on */ });
    return () => { live = false; };
  }, [jobId]);
  const changeHighlights = (on: boolean) => {
    const before = highlightsOn;
    setHighlightsOn(on);
    api.setHighlights(on, currentSession()?.username)
      .then((r) => setHighlightsOn(r.highlights_enabled))
      .catch(() => setHighlightsOn(before));
  };

  const player = useRef<PlayerHandle>(null);
  const descriptionRef = useRef<HTMLTextAreaElement>(null);
  const [playhead, setPlayhead] = useState(0);
  const [taskId, setTaskId] = useState<number | null>(null);
  const [subId, setSubId] = useState<number | null>(null);
  const [loopSubtask, setLoopSubtask] = useState(false);
  const [checklist, setChecklist] = useState(false);
  const [shortcuts, setShortcuts] = useState(false);
  /** The reject reason picker, raised either by the ✕ button or by R (7.1).
   *  Lifted out of SubtaskActions because the keyboard handler lives here —
   *  while it was local, R could only toast "pick a reason in the inspector",
   *  which is a shortcut that describes the action instead of performing it. */
  const [rejecting, setRejecting] = useState(false);
  /** The two by-hand confirmations (6.3), ON THE JOB.
   *
   *  They were component state, then localStorage. Both made them a property of
   *  the machine that ticked them: a second reviewer, or the same person on
   *  another laptop, saw an empty checklist on work that had been confirmed —
   *  and QA, who is the reader most entitled to know what the annotator
   *  confirmed, never saw them at all.
   *
   *  The tick is a claim about the FOOTAGE ("no faces are readable in the
   *  retained frames"), so it belongs to the video. job.manual_checks now
   *  carries it; see db/migrations/001-job-manual-checks.sql.
   *
   *  Rendered from the server's copy with a local override while a write is in
   *  flight, so the box responds to the click rather than to the round trip. */
  const [manualDraft, setManualDraft] = useState<boolean[] | null>(null);
  const serverManual = job.job?.manual_checks;
  const manual = manualDraft
    ?? (serverManual && serverManual.length ? serverManual : [false, false]);

  const toggleManual = useCallback((i: number) => {
    const next = manual.map((v, k) => (k === i ? !v : v));
    setManualDraft(next);
    void api.setManualChecks(jobId, next)
      .then(() => { void job.reload(); setManualDraft(null); })
      .catch((e) => {
        setManualDraft(null);          // put the box back where the server has it
        toast(e instanceof Error ? e.message : String(e), "error");
      });
  }, [manual, jobId, job, toast]);
  const landed = useRef(false);

  const doc = job.job;
  const reload = job.reload;
  /** EDITABLE ONLY WHILE THE VIDEO IS "In progress" — the annotator holds it
   *  and has not submitted it.
   *
   *  It used to be `stage !== "ANNOTATOR"`, which left a SUBMITTED video fully
   *  editable: submit records ANNOTATOR/COMPLETED and the job waits there until
   *  QA claims it, so the stage is still ANNOTATOR and every field, every
   *  verdict button and Re-open stayed live on work already handed over. The
   *  queue got this right — it shows "✓ Submitted to QA" and offers View, not
   *  Continue — and then the screen it opened contradicted it. */
  const readOnly = !!doc && !(doc.current_stage === "ANNOTATOR"
                              && doc.current_status !== "COMPLETED");
  const rules = useMemo(() => rulesFrom(doc?.guideline), [doc?.guideline]);

  const allSubtasks = useMemo(() => (doc?.tasks ?? []).flatMap((t) => t.subtasks), [doc]);

  const task: Task | null = useMemo(() => {
    if (!doc?.tasks.length) return null;
    return doc.tasks.find((t) => t.id === taskId) ?? doc.tasks[0];
  }, [doc, taskId]);

  /** THE SELECTED SUB-TASK, and what happens when it stops existing.
   *
   *  A merge DELETES the follower, so the id in `subId` can vanish under us.
   *  This used to fall back to `task.subtasks[0]` — silently, and to the FIRST
   *  sub-task, which is the one place the annotator certainly was not. Merging
   *  while the video played threw them back to the top of the video; merging
   *  while paused did not, which is what made it look like a playback bug
   *  rather than a selection one.
   *
   *  Falling back by POSITION keeps them where they were working: the index the
   *  vanished sub-task held, clamped to the end of the list. After a merge that
   *  is the survivor, which is exactly the sub-task they just acted on. */
  const lastIndex = useRef(0);
  const sub: Subtask | null = useMemo(() => {
    if (!task?.subtasks.length) return null;
    const found = task.subtasks.find((s) => s.id === subId);
    if (found) {
      lastIndex.current = task.subtasks.indexOf(found);
      return found;
    }
    const at = Math.min(lastIndex.current, task.subtasks.length - 1);
    return task.subtasks[Math.max(0, at)];
  }, [task, subId]);

  /* PER-SEGMENT ROUTING (26 Sep 2026). A sub-task LLM-2 the judge verified is
     READ-ONLY: every text box, stepper and verdict button behaves as in the QA
     view, and split / merge / boundary moves that would touch it are refused
     (the server refuses them too, with 423). The crowd corrects only the
     flagged sub-tasks. */
  const subLocked = !!sub?.locked;
  const editLocked = readOnly || subLocked;

  const select = useCallback((t: Task, s: Subtask, seek = false) => {
    setTaskId(t.id);
    setSubId(s.id);
    if (seek) {
      player.current?.seek(s.start_seconds);
      setPlayhead(s.start_seconds);
    }
  }, []);

  /* 3.1.1 — open on the first PENDING sub-task, playhead at its start. Not the
     first sub-task: an annotator returning to a half-done video should land
     where the work is, not at the beginning again. */
  useEffect(() => {
    if (!doc || landed.current) return;
    landed.current = true;

    const land = (d: JobDetail) => {
      for (const t of d.tasks) {
        const first = t.subtasks.find((s) => s.status === "PENDING");
        if (first) { select(t, first, true); return; }
      }
      const t = d.tasks[0];
      if (t?.subtasks[0]) select(t, t.subtasks[0], true);
    };

    // CLAIM BEFORE LANDING, and reload what comes back. Opening a job that the
    // AI has finished moves it to ANNOTATOR/IN_PROGRESS, and that transition is
    // what makes the screen editable. Firing the claim and forgetting it leaves
    // the stale AI/COMPLETED copy on screen, which renders the entire
    // workspace read-only and labels the submit button "Submitted ✓" on a job
    // nobody has touched.
    const claimable =
      (doc.current_stage === "AI" && doc.current_status === "COMPLETED") ||
      doc.current_stage === "ANNOTATOR";

    if (claimable) {
      void api.start(jobId)
        .then((d) => { land(d); void reload(); })
        .catch(() => land(doc));   // held by someone else — open it read-only
    } else {
      land(doc);
    }
  }, [doc, jobId, select, reload]);

  /** Which sub-task the PLAYHEAD is inside — not which one is selected.
   *
   *  These are different things (5.1.4) and conflating them is what broke
   *  clicking a block during playback. */
  const playheadSub = useRef<number | null>(null);

  /** True while a split or merge is in flight — see `structural`. */
  const reshaping = useRef(false);

  /* 3.3.5 — playing INTO another sub-task selects it.
   *
   * ON THE CROSSING, NOT ON EVERY TICK. This used to re-select whenever the
   * sub-task under the playhead differed from the selected one, which is true
   * continuously after a manual selection elsewhere — so clicking a block in
   * the timeline while the video played selected it for one frame and the next
   * timeupdate (every ~250ms) put the selection back. Testing reported it as
   * "not able to move to another sub-task by clicking on it", and the click was
   * working perfectly the whole time.
   *
   * Comparing against where the playhead WAS makes this fire once, when the
   * boundary is actually crossed. A manual selection then stands until playback
   * genuinely reaches a different sub-task, which is what 3.3.5 asks for. */
  const onTime = useCallback((t: number) => {
    setPlayhead(t);
    if (!doc) return;
    // NEVER WHILE THE TREE IS BEING RESHAPED. A merge is flush -> merge ->
    // reload, several round trips, and the video does not stop for them. This
    // handler fires every ~250ms against the sub-task list as it was BEFORE the
    // edit, so playback would cross into the very sub-task the server was busy
    // deleting and select it — leaving `subId` pointing at a row that no longer
    // existed by the time the reload landed. See `structural` below.
    if (reshaping.current) return;
    // Never while a field has focus: an in-progress edit keeps its value.
    if (document.activeElement instanceof HTMLTextAreaElement ||
        document.activeElement instanceof HTMLInputElement) return;
    for (const tk of doc.tasks) {
      for (const s of tk.subtasks) {
        if (t >= s.start_seconds && t < s.end_seconds) {
          if (s.id !== playheadSub.current) {
            playheadSub.current = s.id;
            setTaskId(tk.id);
            setSubId(s.id);
          }
          return;
        }
      }
    }
  }, [doc]);

  /* 6.2.1 — the next PENDING sub-task after this one, never backwards, no
     wrap-around. Earlier pending sub-tasks are left for a later sweep. */
  const advance = useCallback(() => {
    if (!doc || !task || !sub) return;
    const i = task.subtasks.findIndex((s) => s.id === sub.id);
    const later = task.subtasks.slice(i + 1).find((s) => s.status === "PENDING");
    if (later) { select(task, later, true); return; }

    const ti = doc.tasks.findIndex((t) => t.id === task.id);
    for (const t of doc.tasks.slice(ti + 1)) {
      const first = t.subtasks.find((s) => s.status === "PENDING");
      if (first) {
        select(t, first, true);
        toast(`Next task — ${t.title}`);
        return;
      }
    }
    toast("Every sub-task has a verdict. Opening the checklist.");
    setChecklist(true);   // 6.2.4
  }, [doc, task, sub, select, toast]);

  /** Jump to the next sub-task with no verdict. Forward from here, then wrap —
   *  unlike `advance`, which is what Approve does and deliberately never goes
   *  backwards. This button is a way of finding the work, so it has to be able
   *  to reach a pending sub-task the annotator skipped past. */
  const nextPending = useCallback(() => {
    if (!doc || !task || !sub) return;
    const flat = doc.tasks.flatMap((t) => t.subtasks.map((s) => ({ t, s })));
    const here = flat.findIndex((x) => x.s.id === sub.id);
    const order = [...flat.slice(here + 1), ...flat.slice(0, here + 1)];
    const next = order.find((x) => x.s.status === "PENDING");
    if (!next) { toast("Every sub-task has a verdict."); return; }
    select(next.t, next.s, true);
  }, [doc, task, sub, select, toast]);

  const approve = useCallback(async () => {
    if (!sub || editLocked) return;
    if (sub.status === "APPROVED") { advance(); return; }  // 6.2.1
    // 6.1.2 — the same check runs on approve, and blocks. NARROWER than the
    // inspector's live feedback, though: `api/checks.py` files a too-brief
    // span as a non-blocking warning (TOO_BRIEF), same as submit, so Approve
    // must not refuse over it either — see `blockingIssues`.
    const issues = blockingIssues(sub, rules);
    if (issues.length) { toast(issues[0], "error"); return; }
    await job.review(sub.id, "APPROVED");
    advance();
  }, [sub, editLocked, rules, job, advance, toast]);

  const reject = useCallback(async (reason: string) => {
    if (!sub || editLocked) return;
    await job.review(sub.id, "REJECTED", reason);
    advance();
  }, [sub, editLocked, job, advance]);

  /* 4.2.6 / 5.2.3 — the steppers and ⌖ move the SAME shared boundaries, under
     the same rules. A pinned edge says so rather than silently doing nothing. */
  const nudgeBoundary = useCallback((edge: "start" | "end", to: number) => {
    if (!task || !sub) return;
    const i = task.subtasks.findIndex((s) => s.id === sub.id);
    const other = edge === "start" ? task.subtasks[i - 1] : task.subtasks[i + 1];
    if (sub.locked || other?.locked) { toast("That edge belongs to a sub-task the AI judge verified — it is read-only."); return; }
    if (edge === "start") {
      if (i === 0) { toast("The first sub-task starts where the task starts — that edge is fixed."); return; }
      void job.moveBoundary(task.subtasks[i - 1].id, Number(to.toFixed(3)));
    } else {
      if (i === task.subtasks.length - 1) { toast("The last sub-task ends where the task ends — that edge is fixed."); return; }
      void job.moveBoundary(sub.id, Number(to.toFixed(3)));
    }
  }, [task, sub, job, toast]);

  /** Run a SHAPE-CHANGING edit without the playhead reselecting underneath it.
   *
   *  Split and merge add and remove rows, and both go through the server and a
   *  full reload while the video keeps playing. `onTime` is held off for the
   *  duration and the selection is pinned to `keepId` afterwards, so the
   *  annotator stays on the sub-task they acted on instead of wherever playback
   *  happened to be when the response landed.
   *
   *  `finally`, not the happy path: a refused merge must not leave the crossing
   *  detector switched off for the rest of the session. */
  const structural = useCallback(
    async (keepId: number, run: () => Promise<void>) => {
      reshaping.current = true;
      try {
        await run();
      } finally {
        setSubId(keepId);
        // The detector's memory is an id, and after a merge that id may be gone.
        // Clearing it makes the next crossing re-arm from wherever the playhead
        // actually is rather than comparing against a deleted row.
        playheadSub.current = null;
        reshaping.current = false;
      }
    }, []);

  const split = useCallback(() => {
    if (!sub || editLocked) return;
    const t = player.current?.time() ?? playhead;
    if (t < sub.start_seconds + MIN_SPLIT_MARGIN || t > sub.end_seconds - MIN_SPLIT_MARGIN) {
      toast(`Move the playhead at least ${MIN_SPLIT_MARGIN}s inside the sub-task to split it.`);
      return;
    }
    // Split keeps the left half under the original id — the half the playhead
    // is in is ambiguous, but the one they were looking at is not.
    void structural(sub.id, () => job.split(sub.id, Number(t.toFixed(3))));
  }, [sub, editLocked, playhead, job, toast, structural]);

  const merge = useCallback(() => {
    if (!sub || !task || editLocked) return;
    if (task.subtasks[task.subtasks.length - 1].id === sub.id) {
      toast("Nothing follows this sub-task to merge with.");
      return;
    }
    const following = task.subtasks[task.subtasks.findIndex((s) => s.id === sub.id) + 1];
    if (following?.locked) { toast("The next sub-task was verified by the AI judge — it is read-only."); return; }
    // THE SURVIVOR KEEPS ITS OWN ID (services.merge_subtask absorbs the next
    // into this one), so pinning the selection to `sub.id` across the edit is
    // exact rather than a guess at where the annotator ought to end up.
    void structural(sub.id, () => job.merge(sub.id));
  }, [sub, task, editLocked, job, toast, structural]);

  /** E moves focus into the description — VISIBLY.
   *
   *  Testing: "keyboard input is registering in the text field without even
   *  navigating the cursor to the text field." It was E doing exactly what 7.1
   *  says it does, and nothing on screen said so. The field can be scrolled out
   *  of view in the inspector, the caret is one thin line, and every keystroke
   *  after it — space, A, R, J, K — went in as text instead of driving the
   *  video. From the annotator's seat the app had started typing on its own.
   *
   *  So the jump announces itself: the field is scrolled into view, ringed for
   *  a moment, and the toast says how to get out. The shortcut is kept because
   *  it is in the sheet and it is how you edit without reaching for the mouse;
   *  what was broken was that it happened invisibly. */
  const focusDescription = useCallback(() => {
    const el = descriptionRef.current;
    if (!el) return;
    el.scrollIntoView({ block: "nearest", behavior: "smooth" });
    el.focus();
    el.classList.add("ring-2", "ring-teal");
    window.setTimeout(() => el.classList.remove("ring-2", "ring-teal"), 1200);
    toast("Editing the description — Esc to leave the field.");
  }, [toast]);

  /* -------------------------------------------------------- shortcuts (7.1) */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = document.activeElement;
      const typing = el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement;

      // 7.1.2: one Esc both blurs the field AND closes what is open.
      if (e.key === "Escape") {
        if (typing) (el as HTMLElement).blur();
        setChecklist(false);
        setShortcuts(false);
        setRejecting(false);
        return;
      }
      if (typing) return;              // everything else is suppressed while typing

      switch (e.key) {
        case " ": e.preventDefault(); player.current?.toggle(); break;
        case "a": case "A": void approve(); break;
        // 7.1 — R REJECTS. It raises the reason picker, because a rejection
        // without a reason is not a rejection this pipeline can act on: the
        // reason is what QA reads and what the crowd queue groups by. It used
        // to toast "pick a reason in the inspector", which told the annotator
        // to go and do by hand the thing they had just asked for.
        case "r": case "R":
          if (editLocked) break;
          if (sub?.status !== "PENDING") { toast("Re-open it first to change the verdict."); break; }
          e.preventDefault();
          setRejecting(true);
          break;
        case "e": case "E": e.preventDefault(); focusDescription(); break;
        // J is PREVIOUS and K is NEXT — per 7.1.1 and the prototype. This is the
        // opposite of vim's convention, and getting it backwards is invisible
        // until an annotator says the arrows feel wrong.
        case "j": case "J": step(-1); break;
        case "k": case "K": step(1); break;
        case "s": case "S": split(); break;
        case "m": case "M": merge(); break;
        case "g": case "G": setChecklist(true); break;
        case "?": setShortcuts(true); break;
        case ",": player.current?.stepFrame(-1); break;
        case ".": player.current?.stepFrame(1); break;
        /* Shift+arrow is the « » buttons' own shortcut — printed in their
           tooltips — so it has to do the same thing they do. It was ±5s while
           the buttons moved by sub-task, which is two controls with one label
           and two behaviours. Plain arrow stays a 1-second nudge. */
        case "ArrowLeft":
          e.preventDefault();
          if (e.shiftKey) player.current?.stepSubtask(-1);
          else player.current?.nudge(-1);
          break;
        case "ArrowRight":
          e.preventDefault();
          if (e.shiftKey) player.current?.stepSubtask(1);
          else player.current?.nudge(1);
          break;
      }

      function step(delta: number) {
        if (!task || !sub) return;
        const i = task.subtasks.findIndex((s) => s.id === sub.id);
        const next = task.subtasks[i + delta];
        if (next) select(task, next, true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [approve, split, merge, task, sub, select, toast, editLocked, focusDescription]);

  /* ------------------------------------------------------------- submit */
  const gate = useMemo(
    () => submitGate(doc?.tasks ?? [], rules, manual),
    [doc?.tasks, rules, manual],
  );

  const submit = useCallback(async () => {
    if (!doc) return;
    if (!gate.ok) { setChecklist(true); return; }   // 6.4.1
    const c = counts(doc.tasks);
    const minutes = (doc.video.duration_seconds ?? 0) / 60;
    const ok = window.confirm(
      `Submit ${displayName(doc.video.s3_path)} to QA?\n\n` +
      `${doc.tasks.length} tasks · ${c.total} sub-tasks ` +
      `(${c.approved} approved, ${c.rejected} rejected)\n` +
      `Checklist: ${gate.done} of ${gate.total} complete\n` +
      `Estimated earnings: ${money(minutes * doc.guideline.rate_per_minute, doc.guideline.currency)}\n\n` +
      `The video becomes read-only after submit.`,
    );
    if (!ok) return;
    try {
      await job.flush();
      await api.submit(jobId);
      toast("Submitted to QA.");
      // Back to the queue PAGE the video was opened from, not page 1 — the
      // same target as "← Back" (lib/queuereturn.ts). Testing, 14 Oct: "when a
      // tester submits a video it is sent to page 1, whatever page it was on".
      navigate(backToQueue(location.search));
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e), "error");
      setChecklist(true);
    }
  }, [doc, gate, job, jobId, navigate, toast, location.search]);

  /* --------------------------------------------------------------- render */
  if (job.loading) return <div className="p-8"><Spinner label="Opening the video…" /></div>;
  if (job.error) return <div className="p-8 text-sm text-verdict-rejected-ink">{job.error}</div>;
  if (!doc || !task || !sub)
    // Reachable by URL for a job the AI has not finished. A bare sentence with
    // no way out reads like a crash; say what is happening and offer the door.
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 p-8">
        <p className="text-sm text-text2">
          The AI has not finished annotating this video yet.
        </p>
        <p className="text-xs text-muted">
          It will open for review once pre-annotation completes.
        </p>
        {/* Same return trip as the header's Back. A reviewer who clicked into
            an unfinished video from page 4 is still on page 4. */}
        <Link to={backToQueue(location.search)}
          className="mt-2 rounded-control border border-line bg-card px-3
          py-1.5 text-sm text-ink hover:bg-ground">← Back to videos</Link>
      </div>
    );

  const c = counts(doc.tasks);
  const subIndex = task.subtasks.findIndex((s) => s.id === sub.id);
  // The merge target, for the tooltip. Undefined on the last sub-task, which is
  // exactly when merge has nothing to do.
  const nextSub = task.subtasks[subIndex + 1];
  /* Ascending sub-task starts across the whole video. Sorted explicitly rather
     than trusting task order: tasks tile in time today, and a rule the UI
     depends on should not rest on that staying true. */
  const boundaries = doc.tasks
    .flatMap((t) => t.subtasks.map((s) => s.start_seconds))
    .sort((a, b) => a - b);
  const v = doc.video;

  return (
    <div className="flex h-full min-h-0 flex-col gap-2 p-3">
      {/* ------------------------------------------------------- header */}
      {/* THREE ZONES, AND THE MIDDLE ONE IS TRULY CENTRED.
          Testing, 17 Sep: the counts, the sub-task map and Next pending should
          sit in the centre of the header.

          `grid-cols-[1fr_auto_1fr]` and not flex with `justify-between` or
          `mx-auto`. Those centre the middle item within the space LEFT OVER,
          so it drifts whenever the video name is long or short — the cluster
          would sit in a different place on every video, which is the opposite
          of what a fixed reference strip is for. Two equal `1fr` side columns
          pin the centre column to the header's true midpoint no matter how wide
          the title or the buttons are.

          The side columns carry `min-w-0` so a long file name truncates instead
          of stretching its column and shoving the centre off-centre. */}
      {/* `minmax(0,auto)` on the CENTRE track, not `auto`. An `auto` track
          cannot shrink below its own min-content, so once the centre zone grew
          past the space available it overflowed ACROSS the side columns and
          covered the ← Back link — the stats strip sat on top of it and ate the
          clicks, so the only way off this screen stopped working while looking
          completely normal. Adding the corrected-by-AI count is what pushed it
          over, but a long enough video name would have done the same.
          `overflow-hidden` is the guarantee: the centre clips itself instead of
          reaching into a neighbour, and the sub-task map it clips is already an
          internally scrolling lane. Caught by e2e/queue.spec.ts point 5. */}
      <header className="grid shrink-0 grid-cols-[minmax(0,1fr)_minmax(0,auto)_minmax(0,1fr)]
        items-center gap-4">
        <div className="flex min-w-0 items-center gap-4">
          {/* A bordered control, not bare text on the background — it was the
              only way off this screen and did not read as clickable.

              "Back", not "Videos" — testing, 17 Sep. The tooltip keeps the
              destination, which is what the old label was carrying: a one-word
              button beside a video's title should say what pressing it DOES,
              and "Videos" next to "Arranging books" read as a second label for
              the thing on screen rather than as the way off it. */}
          {/* BACK TO THE PAGE THEY LEFT, not to page 1 — testing, 21 Sep,
              point 5. This was `to="/"`, which is a path with no query string,
              so the queue's carefully recorded ?page=3 was discarded by the one
              control whose job is to return there. lib/queuereturn.ts. */}
          <Link to={backToQueue(location.search)} title="Back to the video queue"
            className="shrink-0 rounded-control border border-line bg-card px-3 py-1.5
            text-sm text-text2 shadow-card hover:bg-ground">← Back</Link>

          <div className="min-w-0">
            <h1 className="truncate text-sm font-extrabold tracking-[-0.01em] text-ink">
              {displayName(v.s3_path)}
            </h1>
            {jobAcc?.video && (
              <div className="truncate text-[11px] text-muted"
                title={`Accuracy page: ${jobAcc.eval_name} against ${jobAcc.gt_version}`}>
                AI-approved <b className="text-verdict-approved-ink">{jobAcc.video.approved}</b>
                {" · "}crowd <b>{jobAcc.video.subtasks - jobAcc.video.approved}</b>
                {" · "}(a) <b>{pct(jobAcc.video.a_pct)}</b>
                {" · "}(b) <b>{pct(jobAcc.video.b_pct)}</b>
                <span className="text-faint"> · run {jobAcc.eval_name}</span>
              </div>
            )}
            {/* THE METADATA LINE IS GONE — testing, 16 Sep.
                It read "Home, Household & Daily Living / Leisure & Hobbies · no
                session · 03:18 · 960x720 · 30.00 fps". None of it is used while
                annotating: the category is chosen upstream, the session code is
                usually absent, and duration, resolution and frame rate are
                properties of the file rather than anything a judgement turns on.
                It cost a row of header height on the one screen that has none to
                spare, and it sat directly under the heading, which is where the
                eye lands first.

                Still available where it belongs: the queue screen lists category
                and duration per video, and every artifact records the lot. */}
          </div>
        </div>

        {/* CENTRE ZONE — the running score, the whole-video map, Next pending.
            Order is the 17 Sep requirement: statuses first, then the grey
            boxes, then the button "next to where the grey boxes end". Next
            pending lives inside SubtaskMap precisely so that adjacency cannot
            be broken by a later layout change. */}
        <div className="flex min-w-0 items-center justify-center gap-3
          overflow-hidden text-xs">
          {/* font-bold, not font-semibold. These three are the numbers the
              annotator tracks all the way through a video and they were set at
              600 — the same weight as the button labels beside them, so they
              read as part of the same control row rather than as the running
              score. 700 separates them from it. */}
          <span className="font-bold text-verdict-approved-ink">{c.approved} approved</span>
          <span className="font-bold text-verdict-rejected-ink">{c.rejected} rejected</span>
          <span className="font-bold text-muted">{c.pending} pending</span>
          {/* THIS NUMBER MUST MATCH THE QUEUE CARD. It read `decision !==
              AUTO_ACCEPT` and said 75 where the card said 10, because 65 of
              them were labels the AI had already rewritten and finished with.
              Corrections get their own count, in the quiet weight — visible,
              not part of the workload. lib/verdict.ts. */}
          {c.needsPerson > 0 && (
            <span className="shrink-0 whitespace-nowrap text-verdict-flagged-ink">
              ▲ {c.needsPerson} need a human
            </span>
          )}
          {/* GLYPH AND NUMBER ONLY, with the words in the tooltip. Spelled out
              this read "✎ 42 corrected by AI" and the pair was wider than the
              single count it replaced, on a header that had no room to give.
              This one is context rather than workload — it is the number you
              check, not the number you work through — so it is the one that
              gives up its words. */}
          {c.corrected > 0 && (
            <span className="shrink-0 whitespace-nowrap text-muted"
              title={`${c.corrected} sub-task${c.corrected > 1 ? "s were" : " was"}`
                + " rewritten by the AI arbiter. Settled — not work you need to do."}>
              ✎ {c.corrected}
            </span>
          )}
          <SubtaskMap
            tasks={doc.tasks}
            selectedSubtaskId={sub.id}
            onPick={(t, s) => select(t, s, true)}
            onNextPending={nextPending}
            className="min-w-0"
          />
        </div>

        {/* RIGHT ZONE. `justify-end` keeps these pinned to the far edge, so the
            centre column's width is the only thing deciding where the cluster
            sits. */}
        <div className="flex items-center justify-end gap-3 text-xs">
          {/* The tooltip names the thing, not the key. `title="?"` repeated the
              glyph already on the button, so hovering the one control whose
              purpose is not self-evident answered with the character you were
              already looking at. */}
          {/* A KEYBOARD, NOT A QUESTION MARK — testing, 21 Sep. "?" is the
              universal glyph for "help about anything", and this button opens
              one specific thing: the keyboard shortcuts. The icon now names its
              own contents, so the tooltip is confirmation rather than the only
              way to find out.

              An inline SVG and not the ⌨ character (U+2328): that codepoint
              renders as a text glyph on some platforms and a full-colour emoji
              on others, so the one control whose job is to look like a small
              grey icon would have been a blue-and-white picture on a Mac. The
              SVG is the same everywhere and inherits currentColor, so it tracks
              the button's text colour through hover and disabled states. */}
          <Button onClick={() => setShortcuts(true)} title="Keyboard shortcuts"
            aria-label="Keyboard shortcuts">
            <svg viewBox="0 0 24 16" className="h-4 w-6" fill="none"
              stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"
              aria-hidden="true">
              <rect x="0.75" y="0.75" width="22.5" height="14.5" rx="2.5" />
              <path d="M4 4.5h1M8 4.5h1M12 4.5h1M16 4.5h1M20 4.5h0.5
                       M4 8h1M8 8h1M12 8h1M16 8h1M20 8h0.5
                       M7 11.5h10" />
            </svg>
          </Button>
          <Button onClick={() => setChecklist(true)} title="G">
            Review checklist {gate.done}/{gate.total}
          </Button>
          <Button
            variant="primary"
            disabled={readOnly || !gate.ok}
            onClick={() => void submit()}
            title={gate.ok ? "" : "Open the checklist to see what is left"}
          >
            {readOnly ? "Submitted ✓" : "Submit video"}
          </Button>
        </div>
      </header>

      {doc.qa_message && (
        <div className="shrink-0 rounded-field bg-verdict-flagged-bg px-3 py-2
          text-xs text-verdict-flagged-ink ring-1 ring-verdict-flagged-line">
          <span className="font-semibold">QA sent this back:</span> {doc.qa_message}
        </div>
      )}
      {job.saveFailing && (
        <div className="shrink-0 rounded-field bg-verdict-rejected-bg px-3 py-2 text-xs
          text-verdict-rejected-ink ring-1 ring-verdict-rejected-line">
          Changes aren't saving. They are still here — leave the tab open and
          they will go through when the connection comes back.
        </div>
      )}

      {/* MORE AIR ABOVE THE TASK TILES — testing, 18 Sep.

          `mt-2` HERE, not a wider `gap` on the page column: that gap also
          separates the strip from the video and the video from the timeline, so
          widening it would push the video down twice over and cost three times
          the height. This adds 8px in exactly one place.

          8px off a budget with none spare — the workspace needs ~900px of
          viewport and a 1366x768 laptop has ~614px. Affordable only because the
          same day recovered more than it: the tiles lost their second line and
          the strip fell from two rows to 43px. */}
      <div className="mt-2 shrink-0">
        <TaskStrip
          tasks={doc.tasks}
          currentTaskId={task.id}
          selectedSubtaskId={sub.id}
          onPickTask={(t) => t.subtasks[0] && select(t, t.subtasks[0], true)}
          onPickSubtask={(t, s) => select(t, s, true)}
        />
      </div>

      {/* --------------------------------------------------- main row
          60 / 40 — 3 against 2, which is exactly 60%.

          THIS NUMBER IS NOT SETTLED AND THE COMMENT HERE KEPT PRETENDING IT
          WAS. It has moved four times: 69% (matching v1.6) -> 60 -> 65 -> 60,
          asked for 18 Sep. Each time the note declared the new value final.

          It is a preference, and a cheap one to change — the ratio is the only
          thing to edit. Do not write "settled" here again.

          The trade-off is real in both directions. More to the video makes the
          evidence bigger; more to the inspector gives the fields room and, since
          that panel is the one that scrolls when height runs short, directly
          reduces how often it scrolls on a small laptop. 40% hands the
          right-hand column back the width 35% had taken from it
          that are typed into.

          The video is the evidence and every judgement is made by looking at
          it, so it gets the larger share; the fields are read and typed in,
          which needs width but not area. basis + grow rather than fixed
          widths, so the split holds at any window size.

          THE TASK CARD LIVES IN THE LEFT COLUMN NOW, as its first row. That
          puts TASK directly opposite SUB-TASK in the inspector — the two
          headings start on the same line, which is what makes them read as
          one level and its child rather than two unrelated bands. */}
      <div className="flex min-h-0 min-w-0 flex-1 gap-3">
        <div className="flex min-h-0 min-w-0 flex-[3] basis-0 flex-col gap-2">
          <Player
            ref={player}
            src={doc.media_url}
            fallbackSrc={doc.media_fallback_url ?? null}
            highlights={highlights}
            subtasks={allSubtasks}
            poster={doc.poster_url ?? null}
            variant={doc.media_variant ?? "original"}
            fps={v.fps ?? 30}
            height={v.height ?? null}
            duration={v.duration_seconds ?? 0}
            /* BOTH MODES LOOP; THEY DIFFER IN SCOPE. See lib/playback.ts —
               TASK used to pass null and run to the end of the VIDEO. */
            loopSpan={loopSpanFor(loopSubtask ? "subtask" : "task", task, sub)}
            loopMode={loopSubtask ? "subtask" : "task"}
            /* « and » step by SUB-TASK, not by a fixed 5 seconds — testing,
               21 Sep: "forward on the first sub-task plays the third". Five
               seconds is an arbitrary distance here; real sub-tasks run 1.2s to
               5.4s, so the jump crossed one, two or none depending on where you
               were. Every start in the VIDEO, not just this task, so » walks off
               the end of a task into the next one rather than stopping dead. */
            boundaries={boundaries}
            /* PLAY STARTS AT THE SELECTED SUB-TASK, NOT WHEREVER THE PLAYHEAD
               HAPPENS TO BE.

               Testing, 16 Sep: "no matter which subtask is selected, clicking
               play always starts playing from the start of the video."

               In TASK loop mode the span is the whole task, and for a video
               whose tasks tile it end to end that span begins at 0 — so a
               playhead sitting at or past the end wrapped to the beginning of
               the video the instant playback started. Selecting a sub-task in
               between made no difference, because nothing tied PLAY to the
               selection.

               This does. Player seeks to `playFrom` before starting, but ONLY
               when the playhead is outside that span — so pausing mid-sub-task
               and pressing play again still resumes where it stopped, which is
               what pause is for. */
            playFrom={sub
              ? { start: sub.start_seconds, end: sub.end_seconds }
              : null}
            onLoopMode={(m) => setLoopSubtask(m === "subtask")}
            highlightsOn={highlightsOn}
            onHighlightsChange={changeHighlights}
            onTime={onTime}
          />
        </div>

        {/* 40% — flex-[2] against the video column's flex-[3].
            The verdict bar is a SIBLING of the panel, not a child of it:
            testing asked for those three buttons outside the sub-task section,
            and outside means outside the card. The column owns the 40%; the
            panel scrolls within it and the bar stays put at the bottom. */}
        {/* THE TASK CARD SITS ABOVE THE SUB-TASK PANEL, IN THIS COLUMN.
            It was the left column's first row, which cost the video that height
            — and the video is the evidence every judgement is made from, so it
            is the one thing on this screen that should get the spare pixels.
            Here it stacks with the panel it names: task, then the sub-task
            inside it, then the verdict on that sub-task, top to bottom.

            A full-width band above BOTH columns would have looked the same and
            given the video nothing: the main row would simply shrink by the
            height the band took. The gain comes from leaving the left column. */}
        <div className="flex min-h-0 min-w-0 flex-[2] basis-0 flex-col gap-2">
          <TaskCard
            task={task}
            index={doc.tasks.findIndex((t) => t.id === task.id)}
            total={doc.tasks.length}
            readOnly={readOnly}
            onEdit={(patch) => void job.editTask(task.id, patch)}
          />
          <Inspector
            sub={sub}
            gtMatch={isQa ? gtMatch.get(sub.id) : undefined}
            siblings={task.subtasks}
            index={subIndex}
            guideline={doc.guideline}
            playhead={playhead}
            readOnly={editLocked}
            descriptionRef={descriptionRef}
            onEdit={(patch) => job.editText(sub.id, patch)}
            onNudgeBoundary={nudgeBoundary}
            onReopen={() => void job.review(sub.id, "PENDING")}
            onPrev={() => {
              const prev = task.subtasks[subIndex - 1];
              if (prev) select(task, prev, true);
            }}
            onNext={() => {
              const next = task.subtasks[subIndex + 1];
              if (next) select(task, next, true);
            }}
          />
          <SubtaskActions
            sub={sub}
            readOnly={editLocked}
            rejecting={rejecting}
            onRejecting={setRejecting}
            onApprove={() => void approve()}
            onReject={(r) => void reject(r)}
            onReopen={() => void job.review(sub.id, "PENDING")}
            onResetToAi={() => void job.resetToAi(sub.id)}
          />

          {/* NO CHECKLIST BAR AND NO SHORTCUT ROW HERE — testing, 24 Sep.
              Both sat under the verdict buttons and cost the column the height
              that made it scroll. Neither is gone from the screen: the checklist
              is the header's "Review checklist n/8" button (and G), and the keys
              are the keyboard icon beside it (and ?). */}
        </div>
      </div>

      {/* ------------------------------------------------------ timeline */}
      <div className="shrink-0">
        <Timeline
          task={task}
          selectedId={sub.id}
          playhead={playhead}
          /* PICKING A SUB-TASK JUMPS TO ITS START. ALWAYS.
             5.1.4 said selecting is not seeking, and this used to honour that
             while paused and seek only during playback. Two behaviours for one
             gesture, decided by a state the annotator is not thinking about.

             Testing: "when video is playing, user click on another sub-task, at
             that time the video should start from the starting point of that
             sub-task selected and not continue from the old sub-task." The same
             argument applies paused — the panel describes one sub-task while
             the picture shows another, and every other route into a sub-task
             (the strip, the map, the inspector's arrows) already seeks. This
             was the only one that did not.

             playheadSub is set with it so the crossing does not re-fire and
             drag the selection back. */
          onSelect={(s) => {
            setSubId(s.id);
            player.current?.seek(s.start_seconds);
            setPlayhead(s.start_seconds);
            playheadSub.current = s.id;
          }}
          onSeek={(t) => { player.current?.seek(t); setPlayhead(t); }}
          onMoveBoundary={(afterId, at) => {
            const i = task.subtasks.findIndex((s) => s.id === afterId);
            if (task.subtasks[i]?.locked || task.subtasks[i + 1]?.locked) {
              toast("That edge belongs to a sub-task the AI judge verified — it is read-only.");
              return;
            }
            void job.moveBoundary(afterId, at);
          }}
          actions={editLocked ? null : (<>
                <Button
                  onClick={split}
                  title={`Split “${subtaskTitle(sub, task.subtasks)}” in two at `
                    + `${preciseClock(playhead)} (shortcut S)`}
                >Split</Button>
                {/* MERGE IS ALWAYS "THIS ONE AND THE NEXT ONE", and now it
                    says so on the button itself.

                    Testing, 16 Sep: "when a sub task is selected and merge is
                    clicked, the action merges the two subtasks always. Ideally
                    the merge button should always merge the selected subtask
                    [with] the next subtask."

                    That IS what it does — services.merge_subtask absorbs the
                    FOLLOWING sibling into the selected one. What it did not do
                    was make that visible: the button looked identical on the
                    last sub-task, where there is nothing to merge with, and
                    only answered with a toast AFTER the click. A control that
                    looks available and then declines reads as a control that
                    did something unpredictable.

                    Disabled when nothing follows, and the label names the pair
                    it will join, so the outcome is legible before committing to
                    it rather than reported afterwards. */}
                <Button
                  onClick={merge}
                  disabled={!nextSub}
                  title={nextSub
                    ? `Merge “${subtaskTitle(sub, task.subtasks)}” with the next `
                      + `sub-task, “${subtaskTitle(nextSub, task.subtasks)}” — the `
                      + `second one's labels are discarded (shortcut M)`
                    : "This is the last sub-task in the task — nothing follows "
                      + "it to merge with"}
                >
                  {/* "Merge", not "Merge with next" — testing, 17 Sep, asked for
                      the short label back. The behaviour it described is
                      unchanged and still explained by the tooltip, which names
                      both sub-tasks; the button stays disabled when nothing
                      follows, which is what actually makes the rule visible. */}
                  Merge
                </Button>
                <Button
                  onClick={() => void job.undo()}
                  disabled={!job.canUndo}
                  title={job.canUndo
                    ? `Undo the last ${job.undoLabel} — restores the spans, the `
                      + "labels and the approvals"
                    : "Nothing to undo"}
                >↶ Undo</Button>
          </>)}
        />
      </div>

      <ChecklistDrawer
        open={checklist}
        onClose={() => setChecklist(false)}
        checks={gate.checks}
        manualLabels={rules.manualChecks}
        manual={manual}
        onToggleManual={toggleManual}
        onSubmit={() => void submit()}
        canSubmit={!readOnly && gate.ok}
      />
      <ShortcutSheet open={shortcuts} onClose={() => setShortcuts(false)} />
    </div>
  );
}
